import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const INITIAL_LABEL = 'Uhr + Armband in den Warenkorb';
const UNCERTAIN_MESSAGE = 'Das Hinzufügen konnte nicht eindeutig bestätigt werden. Bitte prüfe Deinen Warenkorb, bevor Du erneut hinzufügst.';
const REFRESH_ERROR_MESSAGE = 'Der aktuelle Warenkorb konnte nicht geladen werden. Ob Uhr und Armband hinzugefügt wurden, ist deshalb nicht sicher. Bitte prüfe Deinen Warenkorb, bevor Du erneut hinzufügst.';

class MilingiLookBundle extends HTMLElement {
  #listeners;
  #busy = false;

  connectedCallback() {
    if (this.#listeners) return;
    this.#listeners = new AbortController();
    this.addEventListener('submit', this.#onSubmit, { signal: this.#listeners.signal });
    this.#syncButton();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = undefined;
    const button = this.querySelector('[data-bundle-submit]');
    if (button) button.disabled = true;
    // Do not abort an in-flight mutation: it may already have changed the cart.
  }

  #readItems() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return null;
    const data = new FormData(form);
    const items = [];
    for (let index = 0; index < 2; index++) {
      const idKey = `items[${index}][id]`;
      const quantityKey = `items[${index}][quantity]`;
      if (data.getAll(idKey).length !== 1 || data.getAll(quantityKey).length !== 1) return null;
      const id = String(data.get(idKey));
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0) return null;
      if (String(data.get(quantityKey)) !== '1') return null;
      items.push({ id: Number(id), quantity: 1 });
    }
    return items[0].id === items[1].id ? null : items;
  }

  #syncButton() {
    const button = this.querySelector('[data-bundle-submit]');
    if (!button) return;
    button.disabled = !this.isConnected || this.#busy || this.dataset.available !== 'true' || !this.#readItems();
    this.toggleAttribute('aria-busy', this.#busy);
    if (this.#busy) this.setAttribute('aria-busy', 'true');
  }

  #setLabel(text) {
    const label = this.querySelector('[data-bundle-label]');
    if (label) label.textContent = text;
  }

  #setStatus(text) {
    const status = this.querySelector('[data-bundle-status]');
    if (!status) return;
    status.textContent = text;
    status.hidden = false;
  }

  #reportError(message, code) {
    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  #onSubmit = async (event) => {
    if (event.target !== this.querySelector('form')) return;
    event.preventDefault();
    if (this.#busy) return;
    const items = this.#readItems();
    if (this.dataset.available !== 'true' || !items) {
      this.#setStatus('Dieses Set ist aktuell nicht gemeinsam verfügbar. Bitte prüfe die Produkte einzeln.');
      this.#syncButton();
      return;
    }

    this.#busy = true;
    this.#syncButton();
    this.#setLabel('Wird hinzugefügt …');
    this.#setStatus('Uhr und Armband werden gemeinsam hinzugefügt.');

    const deferred = CartLinesUpdateEvent.createPromise();
    // Keep rejection handled even on a template without native cart listeners.
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));

    const sectionIds = [...new Set(
      [...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean)
    )].slice(0, 5);
    let addFailed = false;
    let errorCode = 'SERVICE_UNAVAILABLE';
    let sections;

    try {
      // Exactly one mutation; never retry, clear, or roll back on an ambiguous response.
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (result?.sections && typeof result.sections === 'object' && !Array.isArray(result.sections)) {
        sections = result.sections;
      }
      addFailed = !response.ok || !result || Boolean(result.status || result.errors);
      if (response.status >= 400 && response.status < 500) errorCode = 'INVALID';
    } catch {
      addFailed = true;
    }

    try {
      // Refresh after every POST outcome, including HTTP, JSON, and network errors.
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Cart refresh failed');
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');

      const hasBothItems = items.every((item) => cart.items.some(
        (line) => String(line.variant_id ?? line.id) === String(item.id) && Number(line.quantity) >= 1
      ));
      const didError = addFailed || !hasBothItems;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          source: 'milingi-shop-the-look',
          sourceId: this.id,
          items: cart.items,
          itemCount: 2,
          sections,
          didError,
        },
      });

      if (didError) {
        this.#setLabel(INITIAL_LABEL);
        this.#setStatus(UNCERTAIN_MESSAGE);
        this.#reportError(UNCERTAIN_MESSAGE, errorCode);
      } else {
        this.#setLabel('Beides hinzugefügt');
        this.#setStatus('Uhr und Armband wurden hinzugefügt.');
      }
    } catch (error) {
      deferred.reject(error);
      this.#setLabel(INITIAL_LABEL);
      this.#setStatus(REFRESH_ERROR_MESSAGE);
      this.#reportError(REFRESH_ERROR_MESSAGE, 'SERVICE_UNAVAILABLE');
    } finally {
      this.#busy = false;
      this.#syncButton();
    }
  };
}

if (!customElements.get('milingi-look-bundle')) {
  customElements.define('milingi-look-bundle', MilingiLookBundle);
}
